import { PopUpMessageContext } from '@hb/react'
import { isPracticeContract } from '@hb/shared'
import { useCallback, useContext, useRef, useState } from 'react'
import { DocumentProps, PasswordResponses } from 'react-pdf'
import { downloadFromStorage } from '../../backend/storage/utils'
import { downloadConsentForm } from '../../hooks/consentForms/useConsentFormDownloadUpload'
import { regenerateConsentFormPdf } from '../../hooks/backend/consentForms/utils'
import { downloadPracticeContract } from '../../hooks/practiceContracts/usePracticeContractDownload'
import { usePdfView } from './context'

export const usePdfDownload = () => {
  const { document } = usePdfView()
  const [downloadLoading, setDownloadLoading] = useState(false)

  const { showMessage } = useContext(PopUpMessageContext)
  const onDownload = useCallback(async () => {
    try {
      const { signedStoragePath } = document ?? {}
      // if (!signedStoragePath) {
      //   throw new Error('No signed storage path')
      // }
      setDownloadLoading(true)
      if (signedStoragePath) {
        await downloadFromStorage(signedStoragePath)
      } else {
        if (!document) {
          throw new Error('No document to download')
        }
        if (isPracticeContract(document)) {
          await downloadPracticeContract(document.id, document.practiceId)
        } else {
          await downloadConsentForm(document.id, document.patientId)
        }
      }
      showMessage({
        type: 'success',
        text: signedStoragePath ? 'Signed PDF downloaded' : 'Consent form downloaded',
      })
    } catch (err) {
      console.error(err)
      showMessage({
        type: 'error',
        text: 'Unable to download signed PDF',
        subText: 'Please contact support',
      })
    } finally {
      setDownloadLoading(false)
    }
  }, [document, showMessage])
  return {
    downloadLoading,
    setDownloadLoading,
    onDownload,
  }
}

export const useRegeneratePdf = () => {
  const { document, onAfterRegenerate } = usePdfView()
  const [regenerateLoading, setRegenerateLoading] = useState(false)
  const { showMessage } = useContext(PopUpMessageContext)

  const onRegenerate = useCallback(async () => {
    if (!document?.signedStoragePath) return
    // Regeneration is a consent-form admin feature; practice contracts have no equivalent.
    if (isPracticeContract(document)) return
    try {
      setRegenerateLoading(true)
      await regenerateConsentFormPdf({ consentFormId: document.id, patientId: document.patientId })
      onAfterRegenerate?.()
      showMessage({ type: 'success', text: 'PDF regenerated successfully' })
    } catch (err) {
      console.error(err)
      showMessage({
        type: 'error',
        text: 'Unable to regenerate PDF',
        subText: 'Please contact support',
      })
    } finally {
      setRegenerateLoading(false)
    }
  }, [document, onAfterRegenerate, showMessage])

  return { regenerateLoading, onRegenerate }
}

export interface UsePdfPassword {
  passwordError: string | null
  requestingPassword: boolean
  handlePassword: (password: string) => void
  onPassword: DocumentProps['onPassword']
  clearRequestingPassword: () => void
}
export const usePdfPassword = (onClose: (() => void) | undefined): UsePdfPassword => {
  const [requestingPassword, setRequestingPassword] = useState(false)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const passwordCallback = useRef<((password: string | null) => void) | null>(null)

  const handlePassword = useCallback(
    (password: string) => {
      if (!password) {
        if (onClose) onClose()
      } else {
        const callback = passwordCallback.current
        if (!callback) {
          setPasswordError('Error: No password callback')
          return
        }
        callback(password)
      }
    },
    [onClose, passwordCallback],
  )

  const onPassword = useCallback((callback: (password: string | null) => void, reason: number) => {
    passwordCallback.current = callback
    switch (reason) {
      case PasswordResponses.NEED_PASSWORD: {
        setRequestingPassword(true)
        break
      }
      case PasswordResponses.INCORRECT_PASSWORD: {
        setPasswordError('Incorrect password')
        break
      }
      default: {
        break
      }
    }
  }, [])

  return {
    passwordError,
    requestingPassword,
    onPassword,
    handlePassword,
    clearRequestingPassword: () => setRequestingPassword(false),
  }
}
